import React from 'react';

export const AnalysisCardSkeleton: React.FC = () => (
  <div className="p-6 rounded-2xl bg-surface-primary border border-default animate-pulse">
    <div className="w-14 h-14 rounded-xl bg-surface-elevated mb-4" />
    <div className="h-5 w-3/4 bg-surface-elevated rounded mb-3" />
    <div className="h-4 w-1/2 bg-surface-elevated rounded mb-4" />
    <div className="h-6 w-20 bg-surface-elevated rounded-lg" />
  </div>
);

export const ChatMessageSkeleton: React.FC = () => (
  <div className="flex gap-3 mb-4 animate-pulse">
    <div className="w-7 h-7 rounded-full bg-surface-elevated flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-4 bg-surface-elevated rounded w-full" />
      <div className="h-4 bg-surface-elevated rounded w-[90%]" />
      <div className="h-4 bg-surface-elevated rounded w-[70%]" />
    </div>
  </div>
);

export const ChartSkeleton: React.FC = () => (
  <div className="rounded-2xl bg-surface-primary border border-default overflow-hidden animate-pulse">
    <div className="p-4 border-b border-default">
      <div className="h-5 w-32 bg-surface-elevated rounded" />
    </div>
    <div className="h-64 bg-surface-primary" />
  </div>
);

export const MetricCardSkeleton: React.FC = () => (
  <div className="p-4 rounded-xl bg-surface-primary border border-default animate-pulse">
    <div className="w-10 h-10 rounded-lg bg-surface-elevated mb-3" />
    <div className="h-3 w-16 bg-surface-elevated rounded mb-2" />
    <div className="h-6 w-24 bg-surface-elevated rounded" />
  </div>
);

export const AnalysisLoadingSkeleton: React.FC = () => (
  <div className="space-y-6 animate-pulse">
    <div className="p-6 rounded-2xl bg-surface-primary border border-default">
      <div className="h-8 w-1/2 bg-surface-elevated rounded mb-4" />
    </div>
    
    <div className="grid grid-cols-4 gap-4">
      <MetricCardSkeleton />
      <MetricCardSkeleton />
      <MetricCardSkeleton />
      <MetricCardSkeleton />
    </div>

    <div className="p-6 rounded-2xl bg-surface-primary border border-default space-y-3">
      <div className="h-5 w-40 bg-surface-elevated rounded mb-4" />
      <div className="h-4 w-full bg-surface-elevated rounded" />
      <div className="h-4 w-[95%] bg-surface-elevated rounded" />
      <div className="h-4 w-[85%] bg-surface-elevated rounded" />
    </div>

    <div className="p-6 rounded-2xl bg-surface-primary border border-default space-y-3">
      <div className="h-5 w-32 bg-surface-elevated rounded mb-4" />
      <div className="h-4 w-full bg-surface-elevated rounded" />
      <div className="h-4 w-[90%] bg-surface-elevated rounded" />
    </div>
  </div>
);

type SkeletonType = 'analysis' | 'card' | 'chat' | 'chart' | 'metric';

interface LoadingSkeletonProps {
  type?: SkeletonType;
  count?: number;
}

const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({ type = 'analysis', count = 1 }) => {
  const components: Record<SkeletonType, React.FC> = {
    analysis: AnalysisLoadingSkeleton,
    card: AnalysisCardSkeleton,
    chat: ChatMessageSkeleton,
    chart: ChartSkeleton,
    metric: MetricCardSkeleton,
  };

  const Component = components[type] || AnalysisLoadingSkeleton;

  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <Component key={index} />
      ))}
    </>
  );
};

export default LoadingSkeleton;
